import React, { useCallback, useMemo } from 'react';

const toInt = (v) => {
  const n = parseInt(String(v ?? 0), 10);
  return Number.isFinite(n) ? n : 0;
};

const sum = (arr, pick) => (Array.isArray(arr) ? arr.reduce((acc, x) => acc + toInt(pick(x)), 0) : 0);

const groupSum = (rows, keyFn, valueFn) => {
  const m = new Map();
  (rows || []).forEach((r) => {
    const k = keyFn(r);
    if (!k) return;
    m.set(k, (m.get(k) || 0) + toInt(valueFn(r)));
  });
  return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
};

const normalizeItemValue = (v) => String(v ?? '').trim().toUpperCase();
const numericItemValue = (v) => {
  const txt = String(v ?? '').trim();
  if (!/^\d+$/.test(txt)) return null;
  const n = parseInt(txt, 10);
  return Number.isFinite(n) ? String(n) : null;
};
const itemValuesMatch = (a, b) => {
  const na = normalizeItemValue(a);
  const nb = normalizeItemValue(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const da = numericItemValue(na);
  const db = numericItemValue(nb);
  return da != null && db != null && da === db;
};

const kpiCard = (label, value, sub) => (
  <div className="rounded-xl border border-slate-200 p-3 bg-white">
    <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
    <div className="mt-1 text-2xl font-extrabold text-slate-900">{value}</div>
    {sub && <div className="mt-1 text-xs text-slate-600">{sub}</div>}
  </div>
);

const ConversionKpiPanel = ({ header, rows, events, orderDataLookup, productsByItem }) => {
  const headerTotalTimeMin = Math.max(0, toInt(header?.total_time_min));
  // Excluir código 101 (Runtime) del cálculo de tiempo muerto
  const downTimeMin = sum(events, (e) => e.code === '101' ? 0 : e.minutes);
  // RUN TIME: prioriza minutos código 101; fallback: total header - downtime
  const runtimeFromCode = sum(events, (e) => e.code === '101' ? e.minutes : 0);
  const runTimeMin = runtimeFromCode > 0
    ? runtimeFromCode
    : headerTotalTimeMin > 0
      ? Math.max(0, headerTotalTimeMin - downTimeMin)
      : 0;
  const totalTimeMin = downTimeMin + runTimeMin;

  const uptime = totalTimeMin > 0 ? runTimeMin / totalTimeMin : 0;
  const dt = totalTimeMin > 0 ? downTimeMin / totalTimeMin : 0;

  const bagsGoodRaw = sum(rows, (r) => r.bags_good);
  const counterRaw = sum(rows, (r) => r.counter);
  const quantityRaw = sum(rows, (r) => r.quantity);
  const scrapRaw = sum(rows, (r) => r.scrap);

  const bagsGood = bagsGoodRaw > 0 ? bagsGoodRaw : counterRaw > 0 ? counterRaw : quantityRaw;
  const quality = counterRaw > 0 ? bagsGoodRaw / counterRaw : 0;
  const bpm = runTimeMin > 0 ? bagsGood / runTimeMin : 0;
  const oeeLite = uptime * quality;
  const useGoodAsSummaryBasis = bagsGoodRaw > 0;
  const summaryQtyOfRow = useCallback((r) => {
    if (useGoodAsSummaryBasis) return toInt(r?.bags_good);
    const counter = toInt(r?.counter);
    if (counter > 0) return counter;
    return toInt(r?.quantity);
  }, [useGoodAsSummaryBasis]);

  const resolveMeta = (row) => {
    const meta = row?.details || {};
    const itemKey = String(row?.item || '').trim();

    let orderMeta = itemKey ? orderDataLookup?.get?.(itemKey) : null;
    if (!orderMeta && itemKey && orderDataLookup?.entries) {
      const found = Array.from(orderDataLookup.entries()).find(([k]) => itemValuesMatch(k, itemKey));
      if (found) orderMeta = found[1];
    }

    let productMeta = itemKey ? productsByItem?.get?.(itemKey) : null;
    if (!productMeta && itemKey && productsByItem?.entries) {
      const found = Array.from(productsByItem.entries()).find(([k]) => itemValuesMatch(k, itemKey));
      if (found) productMeta = found[1];
    }

    return {
      bag_size_group: meta.bag_size_group || orderMeta?.bag_size_group || productMeta?.bag_size_group || '',
      bag_style: meta.bag_style || orderMeta?.bag_style || productMeta?.bag_style || '',
      packing_group: meta.packing_group || orderMeta?.packing_group || productMeta?.packing_group || '',
    };
  };

  const woSummary = useMemo(() => groupSum(rows, (r) => (r.work_order || '').trim(), (r) => summaryQtyOfRow(r)), [rows, summaryQtyOfRow]);
  const sizeSummary = useMemo(() => groupSum(rows, (r) => resolveMeta(r).bag_size_group, (r) => summaryQtyOfRow(r)), [rows, orderDataLookup, productsByItem, summaryQtyOfRow]);
  const styleSummary = useMemo(() => groupSum(rows, (r) => resolveMeta(r).bag_style, (r) => summaryQtyOfRow(r)), [rows, orderDataLookup, productsByItem, summaryQtyOfRow]);
  const packingSummary = useMemo(() => groupSum(rows, (r) => resolveMeta(r).packing_group, (r) => summaryQtyOfRow(r)), [rows, orderDataLookup, productsByItem, summaryQtyOfRow]);

  return (
    <div className="space-y-4">
      <div className="text-sm uppercase tracking-wide text-slate-500 font-semibold">KPIs</div>
      <div className="grid grid-cols-2 gap-3">
        {kpiCard('TOTAL TIME', `${totalTimeMin} min`, `${(totalTimeMin / 60).toFixed(2)} hrs`)}
        {kpiCard('DOWN TIME', `${downTimeMin} min`, `${(downTimeMin / 60).toFixed(2)} hrs`)}
        {kpiCard('RUN TIME', `${runTimeMin} min`, `${(runTimeMin / 60).toFixed(2)} hrs`)}
        {kpiCard('UPTIME %', `${(uptime * 100).toFixed(1)}%`)}
        {kpiCard('% DT', `${(dt * 100).toFixed(1)}%`)}
        {kpiCard('BPM', bpm.toFixed(2))}
        {kpiCard('Quality', `${(quality * 100).toFixed(1)}%`, counterRaw > 0 ? `${bagsGoodRaw}/${counterRaw}` : '0/0')}
        {kpiCard('OEE-lite', `${(oeeLite * 100).toFixed(1)}%`)}
        {kpiCard('Bolsas buenas', bagsGood.toLocaleString())}
        {kpiCard('Scrap', scrapRaw.toLocaleString())}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-sm uppercase tracking-wide text-slate-500 font-semibold">Summary</div>

        <div className="mt-3 grid grid-cols-1 gap-4">
          <div>
            <div className="text-xs font-bold text-slate-700">Por Work Order</div>
            <div className="mt-2 overflow-auto max-h-56 border border-slate-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Work Order</th>
                    <th className="px-3 py-2 text-right font-semibold">Cantidad</th>
                    <th className="px-3 py-2 text-right font-semibold">Horas*</th>
                  </tr>
                </thead>
                <tbody>
                  {woSummary.length === 0 ? (
                    <tr><td colSpan={3} className="px-3 py-3 text-slate-500">—</td></tr>
                  ) : (
                    woSummary.slice(0, 30).map(([k, v]) => (
                      <tr key={k} className="border-t">
                        <td className="px-3 py-2 font-mono">{k}</td>
                        <td className="px-3 py-2 text-right font-semibold">{v.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right text-slate-700">
                          {bagsGood > 0 ? ((runTimeMin * (v / bagsGood)) / 60).toFixed(2) : '0.00'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-1 text-[11px] text-slate-500">
              *Horas estimadas proporcional a bolsas buenas (sin tiempos por WO).
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <div className="text-xs font-bold text-slate-700">Por Bag Size</div>
              <div className="mt-2 overflow-auto max-h-40 border border-slate-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Bag Size</th>
                      <th className="px-3 py-2 text-right font-semibold">Cantidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sizeSummary.length === 0 ? (
                      <tr><td colSpan={2} className="px-3 py-3 text-slate-500">—</td></tr>
                    ) : (
                      sizeSummary.slice(0, 20).map(([k, v]) => (
                        <tr key={k} className="border-t">
                          <td className="px-3 py-2">{k}</td>
                          <td className="px-3 py-2 text-right font-semibold">{v.toLocaleString()}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div className="text-xs font-bold text-slate-700">Por Bag Style</div>
              <div className="mt-2 overflow-auto max-h-40 border border-slate-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Bag Style</th>
                      <th className="px-3 py-2 text-right font-semibold">Cantidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {styleSummary.length === 0 ? (
                      <tr><td colSpan={2} className="px-3 py-3 text-slate-500">—</td></tr>
                    ) : (
                      styleSummary.slice(0, 20).map(([k, v]) => (
                        <tr key={k} className="border-t">
                          <td className="px-3 py-2">{k}</td>
                          <td className="px-3 py-2 text-right font-semibold">{v.toLocaleString()}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div className="text-xs font-bold text-slate-700">Por Packing Group</div>
              <div className="mt-2 overflow-auto max-h-40 border border-slate-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Packing</th>
                      <th className="px-3 py-2 text-right font-semibold">Cantidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packingSummary.length === 0 ? (
                      <tr><td colSpan={2} className="px-3 py-3 text-slate-500">—</td></tr>
                    ) : (
                      packingSummary.slice(0, 20).map(([k, v]) => (
                        <tr key={k} className="border-t">
                          <td className="px-3 py-2">{k}</td>
                          <td className="px-3 py-2 text-right font-semibold">{v.toLocaleString()}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversionKpiPanel;

